<script setup lang="ts">
/**
 * BottomTabBar Component
 * Mobile-native bottom navigation with centered action button
 * - 4 navigation tabs (2 left, 2 right)
 * - Prominent center action button (role-aware)
 * - Upload receipt for all authenticated users
 */

import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { UserRole } from '@/types'

interface Props {
  userRole: UserRole
}

const props = defineProps<Props>()

const emit = defineEmits<{
  action: []
}>()

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

interface TabItem {
  name: string
  label: string
  icon: string
  iconActive: string
  route: string
  badge?: number
}

// Left tabs (before center button)
const leftTabs = computed<TabItem[]>(() => {
  const tabs: TabItem[] = [
    {
      name: props.userRole === 'owner' ? 'OwnerDashboard' : 'Dashboard',
      label: props.userRole === 'owner' ? t('nav.dashboard') : t('nav.home'),
      icon: props.userRole === 'owner' ? 'pi pi-chart-bar' : 'pi pi-home',
      iconActive: props.userRole === 'owner' ? 'pi pi-chart-bar' : 'pi pi-home',
      route: props.userRole === 'owner' ? '/owner/dashboard' : '/dashboard',
    },
    {
      name: 'Challenges',
      label: t('nav.challenges'),
      icon: 'pi pi-trophy',
      iconActive: 'pi pi-trophy',
      route: '/challenges',
    },
  ]
  return tabs
})

// Right tabs (after center button)
const rightTabs = computed<TabItem[]>(() => [
  {
    name: 'TicketStatus',
    label: t('nav.tickets'),
    icon: 'pi pi-receipt',
    iconActive: 'pi pi-receipt',
    route: '/tickets',
  },
  {
    name: 'Profile',
    label: t('nav.profile'),
    icon: 'pi pi-user',
    iconActive: 'pi pi-user',
    route: '/profile',
  },
])

// Center action button config
const centerAction = computed(() => ({
  icon: 'pi pi-camera',
  label: t('mobile.upload'),
  ariaLabel: t('mobile.uploadTicketAria'),
}))

// Check if tab is active
function isTabActive(tab: TabItem): boolean {
  return route.path.startsWith(tab.route)
}

// Navigate to tab using push() to preserve back-stack navigation
function navigateToTab(tab: TabItem) {
  if (!isTabActive(tab)) {
    router.push({ name: tab.name })
  }
}

// Handle center action button
function handleAction() {
  emit('action')
}

// Handle keyboard navigation
function handleKeyDown(event: KeyboardEvent, tab: TabItem) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    navigateToTab(tab)
  }
}

function handleActionKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    handleAction()
  }
}
</script>

<template>
  <nav class="bottom-tab-bar" :aria-label="t('nav.mainNavigation')">
    <!-- Left tabs -->
    <div class="tab-group tab-group-left" role="tablist">
      <button
        v-for="tab in leftTabs"
        :key="tab.name"
        type="button"
        role="tab"
        class="tab-item"
        :class="{ 'tab-active': isTabActive(tab) }"
        :aria-selected="isTabActive(tab)"
        :aria-label="tab.label"
        @click="navigateToTab(tab)"
        @keydown="handleKeyDown($event, tab)"
      >
        <span class="tab-icon">
          <i :class="isTabActive(tab) ? tab.iconActive : tab.icon" aria-hidden="true"></i>
          <span v-if="tab.badge" class="tab-badge">{{ tab.badge }}</span>
        </span>
        <span class="tab-label">{{ tab.label }}</span>
      </button>
    </div>

    <!-- Center action button -->
    <div class="center-action-wrapper">
      <button
        type="button"
        class="center-action-btn"
        :aria-label="centerAction.ariaLabel"
        @click="handleAction"
        @keydown="handleActionKeyDown"
      >
        <span class="center-action-icon">
          <i :class="centerAction.icon" aria-hidden="true"></i>
        </span>
        <span class="center-action-label">{{ centerAction.label }}</span>
      </button>
    </div>

    <!-- Right tabs -->
    <div class="tab-group tab-group-right" role="tablist">
      <button
        v-for="tab in rightTabs"
        :key="tab.name"
        type="button"
        role="tab"
        class="tab-item"
        :class="{ 'tab-active': isTabActive(tab) }"
        :aria-selected="isTabActive(tab)"
        :aria-label="tab.label"
        @click="navigateToTab(tab)"
        @keydown="handleKeyDown($event, tab)"
      >
        <span class="tab-icon">
          <i :class="isTabActive(tab) ? tab.iconActive : tab.icon" aria-hidden="true"></i>
          <span v-if="tab.badge" class="tab-badge">{{ tab.badge }}</span>
        </span>
        <span class="tab-label">{{ tab.label }}</span>
      </button>
    </div>
  </nav>
</template>

<style scoped>
.bottom-tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(var(--mobile-tab-bar-height) + var(--safe-area-bottom));
  padding-bottom: var(--safe-area-bottom);
  padding-left: var(--safe-area-left);
  padding-right: var(--safe-area-right);
  box-sizing: border-box;
  background: var(--glass-bg);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  z-index: 100;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  /* Prevent jumping on mobile scroll */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
  contain: layout style;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

/* Tab groups */
.tab-group {
  display: flex;
  flex: 1;
  justify-content: space-around;
  align-items: stretch;
  height: var(--mobile-tab-bar-height);
}

.tab-group-left {
  padding-right: 32px;
}

.tab-group-right {
  padding-left: 32px;
}

/* Individual tabs */
.tab-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.1875rem;
  padding: 0.375rem 0.25rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--neutral-400);
  transition: color var(--duration-fast) ease-out;
  min-height: var(--mobile-touch-target);
  position: relative;
  -webkit-tap-highlight-color: transparent;
}

.tab-item:focus-visible {
  outline: 2px solid var(--color-pg-blue);
  outline-offset: -2px;
  border-radius: 8px;
}

.tab-item:active {
  color: var(--neutral-600);
}

.tab-active {
  color: var(--color-pg-blue);
}

.tab-icon {
  position: relative;
  font-size: 1.25rem;
  line-height: 1;
  transition: all var(--duration-fast) ease-out;
}

.tab-active .tab-icon {
  transform: scale(1.1);
}

.tab-label {
  font-family: var(--font-sans);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.tab-badge {
  position: absolute;
  top: -4px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: var(--error-500);
  color: white;
  font-size: 0.625rem;
  font-weight: 700;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Center action button */
.center-action-wrapper {
  position: absolute;
  left: 50%;
  bottom: calc(var(--safe-area-bottom) + 8px);
  transform: translateX(-50%);
  z-index: 10;
}

.center-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 64px;
  height: 64px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--color-pg-blue) 0%, var(--color-pg-blue-light) 100%);
  color: white;
  box-shadow:
    0 4px 16px rgba(0, 61, 165, 0.35),
    0 2px 6px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: all var(--duration-fast) ease-out;
  -webkit-tap-highlight-color: transparent;
}

.center-action-btn:hover {
  transform: scale(1.05);
  box-shadow:
    0 6px 20px rgba(0, 61, 165, 0.45),
    0 3px 8px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.center-action-btn:active {
  transform: scale(0.95);
  box-shadow:
    0 2px 8px rgba(0, 61, 165, 0.3),
    0 1px 3px rgba(0, 0, 0, 0.1);
}

.center-action-btn:focus-visible {
  outline: 3px solid rgba(0, 61, 165, 0.5);
  outline-offset: 3px;
}

.center-action-icon {
  font-size: 1.5rem;
  line-height: 1;
}

.center-action-label {
  font-family: var(--font-sans);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

/* Owner variant - gold accent */
:global(.user-role-owner) .center-action-btn {
  background: linear-gradient(135deg, var(--color-pg-gold) 0%, #FFD93D 100%);
  color: #78350F;
  box-shadow:
    0 4px 16px rgba(245, 166, 35, 0.4),
    0 2px 6px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

:global(.user-role-owner) .center-action-btn:hover {
  box-shadow:
    0 6px 20px rgba(245, 166, 35, 0.5),
    0 3px 8px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}
</style>
